import { action, makeObservable, observable, runInAction } from "mobx";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_USER_DETAIL, GET_USER_REALESTATE_ACCESS } from "src/api/user";
import { RootStore } from "src/stores/RootStore";

export interface AccessRow {
    realestateid: string;
    realestatename: string | null;
    realestatenumber: string | null;
    role: number[] | null;
    condominium: boolean | null;
    consolidationid: string | null;
    consolidation: boolean | null;
    deleted: boolean | null;
}

interface RoleEnumerationRow {
    key: number | null;
    label: string | null;
}

interface GetUserRealestateAccessData {
    access: AccessRow[];
    role_enumerations: RoleEnumerationRow[];
}

interface GetUserRealestateAccessVars {
    customerid: string;
    userid: string;
}

interface GetUserDetailData {
    user: Array<{
        name1: string | null;
        name2: string | null;
    }>;
}

interface GetUserDetailVars {
    customerid: string;
    userid: string;
}

export class UserAccessStore {
    rootStore: RootStore;

    isAccessLoading = false;
    accessError: string | null = null;
    accessRows: AccessRow[] = [];
    roleLabelsById: Map<number, string> = new Map();
    userDisplayName: string | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isAccessLoading: observable,
            accessError: observable,
            accessRows: observable,
            roleLabelsById: observable,
            userDisplayName: observable,
            load: action,
            reset: action
        });
    }

    reset = () => {
        this.isAccessLoading = false;
        this.accessError = null;
        this.accessRows = [];
        this.roleLabelsById = new Map();
        this.userDisplayName = null;
        this.rootStore.tenantAccessStore.reset();
    };

    load = async (customerid: string, userid: string) => {
        if (!apolloClientInstance) {
            this.accessError = "Apollo client not initialized.";
            return;
        }

        this.isAccessLoading = true;
        this.accessError = null;
        this.rootStore.tenantAccessStore.reset();

        try {
            const { data } = await apolloClientInstance.query<GetUserRealestateAccessData, GetUserRealestateAccessVars>({
                query: GET_USER_REALESTATE_ACCESS,
                variables: {
                    customerid,
                    userid
                },
                fetchPolicy: "cache-first",
            });

            const roleLabels = new Map<number, string>();
            data?.role_enumerations?.forEach((role) => {
                if (role.key !== null && role.label) {
                    roleLabels.set(role.key, role.label);
                }
            });

            runInAction(() => {
                this.accessRows = data?.access ?? [];
                this.roleLabelsById = roleLabels;
            });
        } catch (error: any) {
            runInAction(() => {
                this.accessError = error?.message || "Unknown error";
                this.isAccessLoading = false;
            });
            return;
        }

        try {
            const { data } = await apolloClientInstance.query<GetUserDetailData, GetUserDetailVars>({
                query: GET_USER_DETAIL,
                variables: {
                    customerid,
                    userid
                },
                fetchPolicy: "cache-first",
                errorPolicy: "all"
            });

            const user = data?.user?.[0];
            const name1 = user?.name1?.trim();
            const name2 = user?.name2?.trim();
            const displayName = name1 && name2 ? `${name1} ${name2}` : name1 || name2 || null;

            runInAction(() => {
                this.userDisplayName = displayName;
            });
        } catch (error: any) {}

        runInAction(() => {
            this.isAccessLoading = false;
        });
    };
}
